import React, { useEffect, useMemo, useState } from 'react';
import { t } from 'i18next';
import { useLoaderData } from 'react-router-dom';
import { SupportedTokens } from '@sovryn/contracts';
import { applyDataAttr, Paragraph, ParagraphSize, ParagraphStyle, SimpleTable, SimpleTableRow, } from '@sovryn/ui';
import { Decimal, Percent } from '@sovryn/utils';
import { useLiquityBaseParams } from '../../../5_pages/ZeroPage/hooks/useLiquityBaseParams';
import { BITCOIN, USD } from '../../../../constants/currencies';
import { useBlockNumber } from '../../../../hooks/useBlockNumber';
import { useGetRBTCPrice } from '../../../../hooks/zero/useGetRBTCPrice';
import { translations } from '../../../../locales/i18n';
import { calculateCollateralRatio } from '../../../../utils/helpers';
import { formatCompactValue, formatValue, decimalic, } from '../../../../utils/math';
import { AmountRenderer } from '../../AmountRenderer/AmountRenderer';
import { SystemModeType } from '../types';
const USD_DISPLAY_PRECISION = 2;
export const ZeroStats = ({ className, dataAttribute }) => {
    const { value: blockNumber } = useBlockNumber();
    const [lineOfCredit, setLineOfCredit] = useState('0');
    const [zusdInStabilityPool, setZusdInStabilityPool] = useState();
    const [zusdSupply, setZusdSupply] = useState();
    const [rbtcInLoc, setRbtcInLoc] = useState(Decimal.ZERO);
    const [zeroPrice, setZeroPrice] = useState();
    const [collateralRatio, setCollateralRatio] = useState('0');
    const [zeroInStabilityPoolPercent, setZeroInStabilityPoolPercent] = useState('0');
    const [isRecoveryMode, setIsRecoveryMode] = useState(false);
    const { price: btcPrice } = useGetRBTCPrice();
    const { minBorrowingFeeRate } = useLiquityBaseParams();
    const { liquity } = useLoaderData();
    const renderRBTCInLoc = useMemo(() => rbtcInLoc && zeroPrice ? (React.createElement(React.Fragment, null,
        React.createElement(AmountRenderer, { value: rbtcInLoc, suffix: BITCOIN, precision: 2, showRoundingPrefix: false, dataAttribute: "zero-statistics-rbtc-in-loc" }),
        ' ',
        "($",
        formatCompactValue(Number(rbtcInLoc) * Number(zeroPrice), 2),
        ")")) : (0), [zeroPrice, rbtcInLoc]);
    const renderZusdSupply = useMemo(() => zusdSupply ? (React.createElement(AmountRenderer, { value: zusdSupply, suffix: SupportedTokens.zusd, precision: USD_DISPLAY_PRECISION, showRoundingPrefix: false, dataAttribute: "zero-statistics-zusd-supply" })) : (0), [zusdSupply]);
    const renderZusdStabilityPool = useMemo(() => zusdInStabilityPool ? (React.createElement(React.Fragment, null,
        React.createElement(AmountRenderer, { value: zusdInStabilityPool, suffix: SupportedTokens.zusd, precision: USD_DISPLAY_PRECISION, showRoundingPrefix: false, dataAttribute: "zero-statistics-zusd-in-stability-pool" }),
        ' ',
        "(",
        zeroInStabilityPoolPercent,
        ")")) : (0), [zusdInStabilityPool, zeroInStabilityPoolPercent]);
    const renderBTCPrice = useMemo(() => btcPrice ? (React.createElement(AmountRenderer, { value: btcPrice, suffix: USD, precision: USD_DISPLAY_PRECISION, showRoundingPrefix: false, dataAttribute: "zero-statistics-zusd-btc-price" })) : (0), [btcPrice]);
    const renderProtocolMode = useMemo(() => (isRecoveryMode ? SystemModeType.RECOVERY : SystemModeType.NORMAL), [isRecoveryMode]);
    const originationFeeRate = useMemo(() => formatValue(minBorrowingFeeRate.mul(100), 2), [minBorrowingFeeRate]);
    useEffect(() => {
        liquity
            .getZUSDInStabilityPool()
            .then(result => setZusdInStabilityPool(decimalic(result.toString())));
        liquity
            .getPrice()
            .then(result => setZeroPrice(decimalic(result.toString())));
        liquity
            .getNumberOfTroves()
            .then(result => setLineOfCredit(result.toString()));
    }, [liquity, blockNumber]);
    useEffect(() => {
        liquity.getTotal().then(result => {
            setZusdSupply(decimalic(result.debt.toString()));
            setRbtcInLoc(decimalic(result.collateral.toString()));
            if (zeroPrice) {
                const recoveryMode = result.collateralRatioIsBelowCritical(zeroPrice.toString());
                setIsRecoveryMode(recoveryMode);
                setCollateralRatio(calculateCollateralRatio(decimalic(result.collateral.toString()), decimalic(result.debt.toString()), decimalic(zeroPrice.toString())).toString());
            }
            if (zusdInStabilityPool) {
                const percent = new Percent(zusdInStabilityPool.div(result.debt.toString()));
                setZeroInStabilityPoolPercent(percent === null || percent === void 0 ? void 0 : percent.toString(2));
            }
        });
    }, [liquity, zeroPrice, zusdInStabilityPool, blockNumber]);
    return (React.createElement("div", Object.assign({ className: className }, applyDataAttr(dataAttribute)),
        React.createElement(Paragraph, { size: ParagraphSize.base, style: ParagraphStyle.normal, className: "mb-3 md:mb-6" }, t(translations.stats.zero.title)),
        React.createElement(SimpleTable, { dataAttribute: "zero-statistics-table", className: "lg:max-w-[23.125rem]" },
            React.createElement(SimpleTableRow, { className: "mb-5", label: t(translations.stats.zero.btcPrice), value: renderBTCPrice }),
            React.createElement(SimpleTableRow, { className: "mb-5", label: t(translations.stats.zero.loc), value: lineOfCredit }),
            React.createElement(SimpleTableRow, { className: "mb-5", label: t(translations.stats.zero.rbtcInLoc), value: renderRBTCInLoc }),
            React.createElement(SimpleTableRow, { className: "mb-5", label: t(translations.stats.zero.zusdSupply), value: renderZusdSupply }),
            React.createElement(SimpleTableRow, { className: "mb-5", label: t(translations.stats.zero.zusdInStabilityPool), value: renderZusdStabilityPool }),
            React.createElement(SimpleTableRow, { className: "mb-5", label: t(translations.stats.zero.totalCollateralRatio), value: `${formatValue(collateralRatio, 2)}%` }),
            React.createElement(SimpleTableRow, { className: "mb-5", label: t(translations.stats.zero.originationFeeRate), value: `${originationFeeRate}%` }),
            React.createElement(SimpleTableRow, { label: t(translations.stats.zero.protocolStatus), value: renderProtocolMode }))));
};
